/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import SelectField from "../../../Components/Masters/SelectField";
import { useUser } from "../../../context/AuthContext";
import Multiline from "../../../Components/Masters/Multiline";
import { get } from "../../../utils/api";
import { FUEL_TYPE_OPTIONS, FUEL_UNIT_BY_TYPE } from "../../../utils/fuelConstants";

const QUANTITY_SOLD_UNITS = ["Pieces", "MT", "KL"];

const unitMenuProps = {
  PaperProps: {
    style: { maxHeight: "55%", overflowY: "auto" },
  },
};

const parseQuantitySold = (initialData) => {
  const raw = initialData?.quantitySold;
  if (typeof raw === "object" && raw !== null) {
    return { value: raw.value ?? "", unit: raw.unit || "MT" };
  }
  if (typeof raw === "string" && raw.trim()) {
    const parts = raw.trim().split(" ");
    if (parts.length >= 2) {
      return { value: parts[0], unit: parts.slice(1).join(" ") };
    }
    return { value: raw, unit: "MT" };
  }
  return { value: "", unit: "MT" };
};

const buildFuelFieldsFromInitial = (initialData) => {
  if (Array.isArray(initialData?.fuelEntries) && initialData.fuelEntries.length > 0) {
    const e0 = initialData.fuelEntries[0];
    return {
      fuelType: e0?.fuelType || e0?.fuelName || "",
      fuelConsumption: e0?.fuelConsumption ?? e0?.quantity ?? "",
      fuelConsumptionUnit: e0?.fuelConsumptionUnit || e0?.unit || "",
    };
  }
  return {
    fuelType: initialData?.fuelType || "",
    fuelConsumption: initialData?.fuelConsumption ?? "",
    fuelConsumptionUnit: initialData?.fuelConsumptionUnit || "",
  };
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  id,
}) => {
  const { user } = useUser();
  const qty = parseQuantitySold(initialData);

  const buildInitialFormData = () => ({
    soldProductDate: toDateInputValue(
      initialData?.soldProductDate || initialData?.createdAtOriginal || initialData?.createdAt
    ),
    productMasterId: initialData?.productMasterId || initialData?.productId?._id || "",
    productId: initialData?.productIdSku || initialData?.productId || "",
    productName: initialData?.productName || "",
    productCategory: initialData?.productCategory || "",
    customerMasterId: initialData?.customerMasterId || "",
    vendorName: initialData?.vendorName || "",
    quantitySold: qty.value,
    quantitySoldUnit: qty.unit,
    locationOfProcessing: initialData?.locationOfProcessing || "",
    processingMethodUsed: initialData?.processingMethodUsed || "",
    electricityConsumption:
      initialData?.electricityConsumption ??
      initialData?.energyConsumptionForProcessing ??
      "",
    ...buildFuelFieldsFromInitial(initialData),
    materialsAddedDuringProcessing: initialData?.materialsAddedDuringProcessing || "",
    remark: initialData?.remark || "",
    documents: initialData?.documents || [],
    editRemarkReason: initialData?.editRemarkReason || "",
  });

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(buildInitialFormData);
  const [formData2, setFormData2] = useState(buildInitialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [productsWithCustomers, setProductsWithCustomers] = useState([]);
  const inlineErrorTextProps = {
    sx: { ml: 0, mr: 0, mt: "4px", fontSize: "0.95rem", lineHeight: 1.2 },
  };

  const disabledFieldSx = {
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "#6b7280",
      cursor: "not-allowed",
    },
    "& .MuiInputBase-root.Mui-disabled": {
      backgroundColor: "#f3f4f6",
    },
  };

  useEffect(() => {
    const next = buildInitialFormData();
    setFormData(next);
    setFormData2(next);
    setUploadedFiles(initialData?.documents || []);
    setErrors({});
  }, [id, initialData, isOpen]);

  useEffect(() => {
    if (!productsWithCustomers.length || formData.productMasterId) return;

    const matchProduct = productsWithCustomers.find(
      (p) =>
        p._id === formData.productMasterId ||
        (formData.productId && p.productId === formData.productId) ||
        (formData.productName && p.productName === formData.productName)
    );
    if (!matchProduct) return;

    const matchCustomer = (matchProduct.customers || []).find(
      (c) =>
        c._id === formData.customerMasterId ||
        (formData.vendorName && c.customerVendorName === formData.vendorName)
    );

    setFormData((prev) => ({
      ...prev,
      productMasterId: matchProduct._id,
      productId: matchProduct.productId || prev.productId,
      productName: matchProduct.productName || prev.productName,
      productCategory: matchProduct.category || prev.productCategory,
      customerMasterId: matchCustomer?._id || prev.customerMasterId,
      vendorName: matchCustomer?.customerVendorName || prev.vendorName,
      locationOfProcessing: matchCustomer?.country || prev.locationOfProcessing,
    }));
  }, [productsWithCustomers]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchProducts = async () => {
      try {
        const res = await get("/master/customerMasters/products-with-customers");
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
        setProductsWithCustomers(list);
      } catch (error) {
        console.error("Error fetching products with customers:", error);
        setProductsWithCustomers([]);
      }
    };
    fetchProducts();
  }, [isOpen]);

  const selectedProduct = useMemo(
    () =>
      productsWithCustomers.find((p) => p._id === formData.productMasterId) || null,
    [productsWithCustomers, formData.productMasterId]
  );

  const customerOptions = useMemo(
    () => selectedProduct?.customers || [],
    [selectedProduct]
  );

  const selectedCustomer = useMemo(
    () => customerOptions.find((c) => c._id === formData.customerMasterId) || null,
    [customerOptions, formData.customerMasterId]
  );

  const processingMethodOptions = useMemo(() => {
    const fromCustomer = selectedCustomer?.processingMethodsUsed;
    if (Array.isArray(fromCustomer) && fromCustomer.length > 0) return fromCustomer;
    return [];
  }, [selectedCustomer]);

  const applySelectedProduct = (product) => {
    if (!product) return;
    setFormData((prev) => ({
      ...prev,
      productMasterId: product._id || "",
      productId: product.productId || "",
      productName: product.productName || "",
      productCategory: product.category || "",
      customerMasterId: "",
      vendorName: "",
      locationOfProcessing: "",
      processingMethodUsed: "",
    }));
    setErrors((prev) => ({
      ...prev,
      productMasterId: "",
      productId: "",
      productName: "",
      customerMasterId: "",
      vendorName: "",
      locationOfProcessing: "",
      processingMethodUsed: "",
    }));
  };

  const applySelectedCustomer = (customer) => {
    if (!customer) return;
    setFormData((prev) => ({
      ...prev,
      customerMasterId: customer._id || "",
      vendorName: customer.customerVendorName || "",
      locationOfProcessing: customer.country || "",
      processingMethodUsed: "",
    }));
    setErrors((prev) => ({
      ...prev,
      customerMasterId: "",
      vendorName: "",
      locationOfProcessing: "",
      processingMethodUsed: "",
    }));
  };

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    const cleared = buildInitialFormData();
    setFormData(cleared);
    setFormData2(cleared);
    onClose();
  };

  const handleClose = () => {
    const isFormDataUnchanged = Object.keys(formData).every((key) => {
      const value1 = formData[key];
      const value2 = formData2[key];
      if (Array.isArray(value1) && Array.isArray(value2)) {
        return JSON.stringify(value1) === JSON.stringify(value2);
      }
      if (
        typeof value1 === "object" &&
        value1 !== null &&
        typeof value2 === "object" &&
        value2 !== null
      ) {
        return JSON.stringify(value1) === JSON.stringify(value2);
      }
      return value1 === value2;
    });

    if (isFormDataUnchanged) handleExitAgree();
    else setEditDialog(true);
  };

  const validateFormData = () => {
    const newErrors = {};

    if (!formData.soldProductDate) {
      newErrors.soldProductDate = "Sold product date is required.";
    }
    if (!formData.productMasterId) {
      newErrors.productMasterId = "Product is required.";
    }
    if (!formData.customerMasterId) {
      newErrors.customerMasterId = "Customer/Vendor Name is required.";
    }
    if (!String(formData.quantitySold ?? "").trim()) {
      newErrors.quantitySold = "Quantity Sold is required.";
    } else if (isNaN(Number(formData.quantitySold)) || Number(formData.quantitySold) < 0) {
      newErrors.quantitySold = "Quantity Sold must be a valid number.";
    }
    if (!formData.quantitySoldUnit) {
      newErrors.quantitySoldUnit = "Unit is required.";
    }
    if (!formData.processingMethodUsed) {
      newErrors.processingMethodUsed = "Processing Method Used is required.";
    }

    const electricityStr = String(formData.electricityConsumption ?? "").trim();
    const fuelTypeStr = String(formData.fuelType ?? "").trim();
    const fuelQtyStr = String(formData.fuelConsumption ?? "").trim();
    const fuelUnitStr = String(formData.fuelConsumptionUnit ?? "").trim();

    const hasElectricity = electricityStr !== "";
    const hasFuelType = fuelTypeStr !== "";
    const hasFuelQtyOrUnit = fuelQtyStr !== "" || fuelUnitStr !== "";
    const usingFuel = hasFuelType || (hasFuelQtyOrUnit && !hasElectricity);
    const usingElectricity = hasElectricity && !hasFuelType;

    if (!usingElectricity && !usingFuel) {
      newErrors.electricityConsumption =
        "Either Electricity Consumption or Fuel Details are required.";
      newErrors.fuelType =
        "Either Electricity Consumption or Fuel Details are required.";
    }

    if (usingElectricity) {
      const electricityNum = Number(electricityStr);
      if (isNaN(electricityNum) || electricityNum < 0) {
        newErrors.electricityConsumption =
          "Electricity Consumption must be a valid number.";
      }
    }

    if (usingFuel) {
      if (!hasFuelType) {
        newErrors.fuelType = "Fuel Name is required.";
      }
      if (hasFuelType && !fuelQtyStr) {
        newErrors.fuelConsumption = "Fuel Quantity is required.";
      } else if (
        fuelQtyStr &&
        (isNaN(Number(formData.fuelConsumption)) || Number(formData.fuelConsumption) < 0)
      ) {
        newErrors.fuelConsumption = "Fuel Quantity must be a valid number.";
      }
      if (hasFuelType && !fuelUnitStr) {
        newErrors.fuelConsumptionUnit = "Fuel Unit is required.";
      }
    }

    if (user?.endUser && isEditing && !formData.editRemarkReason) {
      newErrors.editRemarkReason = "Edit remark is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;

    const fuelEntries = String(formData.fuelType ?? "").trim()
      ? [
          {
            fuelType: formData.fuelType,
            fuelConsumption: formData.fuelConsumption
              ? Number(formData.fuelConsumption)
              : null,
            fuelConsumptionUnit: formData.fuelConsumptionUnit,
          },
        ]
      : [];

    const firstFuel = fuelEntries[0] || {};

    let formDataToSubmit = {
      soldProductDate: formData.soldProductDate,
      productMasterId: formData.productMasterId,
      productId: formData.productId,
      productName: formData.productName,
      productCategory: formData.productCategory,
      customerMasterId: formData.customerMasterId,
      vendorName: formData.vendorName,
      quantitySold: {
        value: formData.quantitySold ? parseFloat(formData.quantitySold) : null,
        unit: formData.quantitySoldUnit || "MT",
      },
      locationOfProcessing: formData.locationOfProcessing,
      processingMethodUsed: formData.processingMethodUsed,
      electricityConsumption: formData.electricityConsumption
        ? Number(formData.electricityConsumption)
        : "",
      energyConsumptionForProcessing: formData.electricityConsumption
        ? Number(formData.electricityConsumption)
        : "",
      fuelEntries,
      fuelType: firstFuel.fuelType || "",
      fuelConsumption: firstFuel.fuelConsumption ?? "",
      fuelConsumptionUnit: firstFuel.fuelConsumptionUnit || "",
      fuelType2: "",
      fuelConsumption2: "",
      fuelConsumptionUnit2: "",
      energySourceForProcessing: fuelEntries
        .map((f) => `${f.fuelType || ""}`.trim())
        .filter(Boolean)
        .join(", "),
      materialsAddedDuringProcessing: formData.materialsAddedDuringProcessing,
      remark: formData.remark,
      documents: uploadedFiles,
    };

    if (user?.endUser && isEditing) {
      formDataToSubmit.requestStatus = "edit";
      formDataToSubmit.requestMode = true;
      formDataToSubmit.approvalStatus = "Pending";
      formDataToSubmit.editRemarkReason = formData.editRemarkReason;
    }

    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNumericChange = (field) => (event) => {
    const { value } = event.target;
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleElectricityChange = (event) => {
    const { value } = event.target;
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, electricityConsumption: value }));
    setErrors((prev) => ({
      ...prev,
      electricityConsumption: "",
      fuelType: "",
      fuelConsumption: "",
      fuelConsumptionUnit: "",
    }));
  };

  const handleFuelTypeChange = (event) => {
    const { value } = event.target;
    const cleared = value === "" || value === "Tap to Select";

    if (cleared) {
      setFormData((prev) => ({
        ...prev,
        fuelType: "",
        fuelConsumption: "",
        fuelConsumptionUnit: "",
      }));
      setErrors((prev) => ({
        ...prev,
        fuelType: "",
        fuelConsumption: "",
        fuelConsumptionUnit: "",
      }));
      return;
    }

    const fuelQty = String(formData?.fuelConsumption ?? "").trim();
    setFormData((prev) => ({
      ...prev,
      fuelType: value,
      fuelConsumptionUnit: "",
    }));
    setErrors((prev) => ({
      ...prev,
      fuelType: "",
      fuelConsumptionUnit: "",
      electricityConsumption: "",
      fuelConsumption: value && !fuelQty ? "Fuel Quantity is required." : "",
    }));
  };

  const handleFuelConsumptionChange = (field) => (event) => {
    const { value } = event.target;

    if (field === "qty" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field === "qty" ? "fuelConsumption" : "fuelConsumptionUnit"]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field === "qty" ? "fuelConsumption" : "fuelConsumptionUnit"]: "",
      electricityConsumption: "",
    }));
  };

  const renderFuelFields = () => (
    <>
      <Box sx={{ margin: "1rem 0" }}>
        <SelectField
          title="Fuel Name"
          name="fuelType"
          value={formData.fuelType}
          handleChange={handleFuelTypeChange}
          options={FUEL_TYPE_OPTIONS}
          errors={errors.fuelType}
          allowClear
        />
      </Box>
      <Box sx={{ margin: "1rem 0" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
          Fuel Quantity
        </Typography>
        <Box display="flex" gap={1} sx={{ width: "100%" }}>
          <Box sx={{ width: "60%" }}>
            <TextField
              type="text"
              size="small"
              fullWidth
              placeholder="Enter consumption"
              value={formData.fuelConsumption || ""}
              onChange={handleFuelConsumptionChange("qty")}
              error={Boolean(errors.fuelConsumption)}
              helperText={errors.fuelConsumption || ""}
              FormHelperTextProps={inlineErrorTextProps}
            />
          </Box>
          <Box sx={{ width: "40%" }}>
            <TextField
              select
              size="small"
              fullWidth
              name="fuelConsumptionUnit"
              value={formData.fuelConsumptionUnit || ""}
              onChange={handleFuelConsumptionChange("unit")}
              disabled={!formData.fuelType}
              error={Boolean(errors.fuelConsumptionUnit)}
              helperText={errors.fuelConsumptionUnit || ""}
              FormHelperTextProps={inlineErrorTextProps}
              SelectProps={{ MenuProps: unitMenuProps, displayEmpty: true }}
            >
              <MenuItem value="" disabled>
                Unit
              </MenuItem>
              {(FUEL_UNIT_BY_TYPE[formData.fuelType] || []).map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </Box>
    </>
  );

  const renderDisabledField = ({ title, value, placeholder }) => (
    <Box sx={{ margin: "1rem 0" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        {title}
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={value || ""}
        disabled
        placeholder={placeholder}
        sx={disabledFieldSx}
      />
    </Box>
  );

  const renderSelect = ({
    title,
    name,
    value,
    options,
    onChange,
    disabled = false,
    errorKey,
  }) => (
    <Box sx={{ margin: "1rem 0" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        {title}
      </Typography>
      <TextField
        select
        size="small"
        fullWidth
        disabled={disabled}
        value={value || "Tap to select"}
        onChange={onChange}
        error={Boolean(errors[errorKey || name])}
        helperText={
          <Typography variant="body2" color="#D34040" sx={{ marginLeft: "-0.8rem" }}>
            {errors[errorKey || name] || ""}
          </Typography>
        }
        SelectProps={{
          MenuProps: { PaperProps: { sx: { maxHeight: 280 } } },
        }}
      >
        <MenuItem value="Tap to select" disabled>
          Tap to select
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box sx={{ width: "26rem", display: "flex", justifyContent: "space-between", gap: "1rem" }}>
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {/* Sold product date */}
            <Box sx={{ margin: "1rem 0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                Sold Product Date
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={formData.soldProductDate || ""}
                onChange={handleChange("soldProductDate")}
                error={Boolean(errors.soldProductDate)}
                helperText={
                  <Typography variant="body2" color="#D34040" sx={{ marginLeft: "-0.8rem" }}>
                    {errors.soldProductDate || ""}
                  </Typography>
                }
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
              />
            </Box>

            {/* Product ID */}
            {renderSelect({
              title: "Product ID",
              name: "productMasterId",
              value: formData.productMasterId,
              errorKey: "productMasterId",
              disabled: productsWithCustomers.length === 0,
              options: productsWithCustomers.map((p) => ({
                value: p._id,
                label: p.productId || p.productName,
              })),
              onChange: (e) => {
                const val = e.target.value;
                if (val === "Tap to select") return;
                const product = productsWithCustomers.find((p) => p._id === val);
                applySelectedProduct(product);
              },
            })}

            {renderDisabledField({
              title: "Product Name",
              value: formData.productName,
              placeholder: "Auto-filled from product",
            })}

            {renderDisabledField({
              title: "Product Category",
              value: formData.productCategory,
              placeholder: "Auto-filled from product",
            })}

            {/* Customer / Vendor */}
            {renderSelect({
              title: "Customer/Vendor Name",
              name: "customerMasterId",
              value: formData.customerMasterId,
              errorKey: "customerMasterId",
              disabled: !formData.productMasterId,
              options: customerOptions.map((c) => ({
                value: c._id,
                label: c.customerVendorName,
              })),
              onChange: (e) => {
                const val = e.target.value;
                if (val === "Tap to select") return;
                const customer = customerOptions.find((c) => c._id === val);
                applySelectedCustomer(customer);
              },
            })}

            {/* Quantity Sold */}
            <Box sx={{ margin: "1rem 0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                Quantity Sold (Pieces/MT/KL)
              </Typography>
              <Box sx={{ display: "flex", gap: "0.75rem" }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter quantity"
                  value={formData.quantitySold || ""}
                  onChange={handleNumericChange("quantitySold")}
                  error={Boolean(errors.quantitySold)}
                  helperText={errors.quantitySold || ""}
                  FormHelperTextProps={inlineErrorTextProps}
                />
                <TextField
                  select
                  size="small"
                  sx={{ width: "42%" }}
                  value={formData.quantitySoldUnit || ""}
                  onChange={handleChange("quantitySoldUnit")}
                  error={Boolean(errors.quantitySoldUnit)}
                  SelectProps={{ displayEmpty: true, MenuProps: unitMenuProps }}
                >
                  <MenuItem value="" disabled>
                    Unit
                  </MenuItem>
                  {QUANTITY_SOLD_UNITS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            {renderDisabledField({
              title: "Location of Processing (Country)",
              value: formData.locationOfProcessing,
              placeholder: "Auto-filled from customer",
            })}

            {/* Processing method */}
            {renderSelect({
              title: "Processing Method Used",
              name: "processingMethodUsed",
              value: formData.processingMethodUsed,
              errorKey: "processingMethodUsed",
              disabled: !formData.customerMasterId,
              options: processingMethodOptions.map((m) => ({ value: m, label: m })),
              onChange: handleChange("processingMethodUsed"),
            })}

            {/* Electricity */}
            <Box sx={{ margin: "1rem 0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                {formatTitleWithOptional("Electricity Consumption (KWH)")}
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter electricity consumption"
                value={formData.electricityConsumption || ""}
                onChange={handleElectricityChange}
                error={Boolean(errors.electricityConsumption)}
                helperText={errors.electricityConsumption || ""}
                FormHelperTextProps={inlineErrorTextProps}
              />
            </Box>

            {renderFuelFields()}

            {/* Materials */}
            <Box sx={{ margin: "1rem 0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                {formatTitleWithOptional("Materials Added During Processing (MT)")}
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter materials"
                value={formData.materialsAddedDuringProcessing || ""}
                onChange={handleChange("materialsAddedDuringProcessing")}
              />
            </Box>

            {/* Remark */}
            <Box sx={{ margin: "1rem 0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                {formatTitleWithOptional("Remark")}
              </Typography>
              <TextField
                multiline
                rows={3}
                size="small"
                fullWidth
                placeholder="Remark"
                value={formData.remark || ""}
                onChange={handleChange("remark")}
              />
            </Box>

            {user?.endUser && isEditing && (
              <Multiline
                title={formatTitleWithOptional("Edit Remark Reason")}
                placeholder="Additional edit remarks"
                fieldName="editRemarkReason"
                value={formData.editRemarkReason}
                handleChange={handleChange("editRemarkReason")}
                errors={errors.editRemarkReason}
              />
            )}

            <FileField
              title={formatTitleWithOptional("Upload Documents (optional)")}
              name="document"
              fileUrls={uploadedFiles}
              progress={uploadProgress.document || []}
              {...{ setUploadedFiles, setUploadProgress, setErrors }}
            />
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: "#1C486B",
              textTransform: "none",
              mt: 2,
            }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;
